import type { BroadcastState } from "@riq/api";

export type BroadcastLivenessSignal =
  | { readonly type: "session-started" }
  | { readonly type: "session-dropped" }
  | { readonly type: "peer-failed" }
  | { readonly type: "broadcast-ended"; readonly current: BroadcastState }
  | { readonly type: "poll"; readonly snapshot: BroadcastState; readonly sessionUp: boolean }
  | { readonly type: "poll-failed" }
  | { readonly type: "track-check"; readonly snapshot: BroadcastState; readonly trackLive: boolean }
  | { readonly type: "track-check-failed"; readonly trackLive: boolean };

export type BroadcastLivenessVerdict =
  | { readonly type: "keep" }
  | { readonly type: "show"; readonly snapshot: BroadcastState }
  | { readonly type: "end"; readonly snapshot: BroadcastState }
  | { readonly type: "drop-session" };

const KEEP: BroadcastLivenessVerdict = { type: "keep" };
const DROP: BroadcastLivenessVerdict = { type: "drop-session" };

function isUnknown(snapshot: BroadcastState): boolean {
  return typeof snapshot.error === "string" && snapshot.error.length > 0;
}

/** Offline snapshot for BROADCAST_ENDED. Keeps isDemo, title, and speaker. */
function endedBroadcast(current: BroadcastState): BroadcastState {
  const ended: BroadcastState = {
    isLive: false,
    isDemo: current.isDemo,
    activeListeners: 0,
  };
  if (current.title != null) ended.title = current.title;
  if (current.speaker != null) ended.speaker = current.speaker;
  return ended;
}

/**
 * Decides whether a Broadcast is still on, has ended, or this Egress Session
 * alone was lost. Remembers the offline streak. Does not fetch or hold a peer.
 */
export class BroadcastLiveness {
  /** Consecutive clean offlines seen while a session is up. */
  private streak = 0;

  decide(signal: BroadcastLivenessSignal): BroadcastLivenessVerdict {
    switch (signal.type) {
      case "session-started":
      case "session-dropped":
      case "poll-failed":
        return this.keep();
      case "peer-failed":
        this.streak = 0;
        return DROP;
      case "broadcast-ended":
        this.streak = 0;
        return { type: "end", snapshot: endedBroadcast(signal.current) };
      case "poll":
        return this.onPoll(signal.snapshot, signal.sessionUp);
      case "track-check-failed":
        return this.onDeadTrack(signal.trackLive);
      case "track-check":
        return this.onTrackCheck(signal.snapshot, signal.trackLive);
    }
  }

  private onPoll(snapshot: BroadcastState, sessionUp: boolean): BroadcastLivenessVerdict {
    if (isUnknown(snapshot)) return this.keep();
    if (snapshot.isLive || !sessionUp) {
      this.streak = 0;
      return { type: "show", snapshot };
    }
    if (this.streak === 0) {
      this.streak = 1;
      return KEEP;
    }
    this.streak = 0;
    return { type: "end", snapshot };
  }

  private onTrackCheck(snapshot: BroadcastState, trackLive: boolean): BroadcastLivenessVerdict {
    if (isUnknown(snapshot)) return this.onDeadTrack(trackLive);
    if (!snapshot.isLive) {
      this.streak = 0;
      return { type: "end", snapshot };
    }
    this.streak = 0;
    if (!trackLive) return DROP;
    return { type: "show", snapshot };
  }

  /** Live, error, or thrown track check. A recovered track cancels the drop. */
  private onDeadTrack(trackLive: boolean): BroadcastLivenessVerdict {
    this.streak = 0;
    return trackLive ? KEEP : DROP;
  }

  private keep(): BroadcastLivenessVerdict {
    this.streak = 0;
    return KEEP;
  }
}
